import { useEffect, useState } from 'react';
import { 
  Package, 
  Users, 
  ShoppingCart, 
  DollarSign, 
  TrendingUp, 
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple bar chart component
function SalesChart({ data }) {
  const maxSales = Math.max(...data.map(d => d.sales), 1);
  const totalSales = data.reduce((sum, d) => sum + d.sales, 0);
  
  if (totalSales === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-gray-400 mt-4">
        <div className="text-center">
          <p>Pa gen done lavant pou 7 jou sa yo</p>
          <p className="text-sm mt-2">Kòmann peye yo pral parèt isit la</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex items-end justify-between gap-2 h-48 mt-4">
      {data.map((item, index) => (
        <div key={index} className="flex-1 flex flex-col items-center gap-2">
          <div 
            className="w-full bg-yellow-500 rounded-t transition-all hover:bg-yellow-400"
            style={{ 
              height: `${(item.sales / maxSales) * 100}%`,
              minHeight: item.sales > 0 ? '8px' : '2px'
            }}
          />
          <span className="text-xs text-gray-500">{item.date}</span>
        </div>
      ))}
    </div>
  );
}

function StatCard({ title, value, icon: Icon, trend, trendValue, color = "blue" }) {
  const colors = {
    blue: "bg-blue-500",
    green: "bg-green-500",
    yellow: "bg-yellow-500",
    red: "bg-red-500",
    purple: "bg-purple-500"
  };

  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-2xl font-bold mt-2 text-[#001A3E]">{value}</p>
            {trend && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                {trend === 'up' ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                <span>{trendValue}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colors[color]}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, {
        headers: getAuthHeader()
      });
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#001A3E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#001A3E]">Dashboard</h1>
        <p className="text-gray-500">Byenveni nan Admin Panel MRC Boutique</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Pwodwi"
          value={stats?.total_products || 0}
          icon={Package}
          color="blue"
        />
        <StatCard
          title="Total Kliyan"
          value={stats?.total_customers || 0}
          icon={Users}
          color="green"
        />
        <StatCard
          title="Total Kòmann"
          value={stats?.total_orders || 0}
          icon={ShoppingCart}
          color="purple"
        />
        <StatCard
          title="Revni Total"
          value={`$${(stats?.total_revenue || 0).toLocaleString()}`}
          icon={DollarSign}
          color="yellow"
        />
      </div>

      {/* Sales Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-gray-500">Lavant Jodi a</p>
            <p className="text-2xl font-bold mt-1 text-[#001A3E]">
              ${(stats?.today_sales || 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-gray-500">Lavant Semèn nan</p>
            <p className="text-2xl font-bold mt-1 text-[#001A3E]">
              ${(stats?.week_sales || 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-gray-500">Lavant Mwa a</p>
            <p className="text-2xl font-bold mt-1 text-[#001A3E]">
              ${(stats?.month_sales || 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts and Stock Alert */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#001A3E]">
              <TrendingUp className="h-5 w-5" />
              Grafik Lavant (7 Jou)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.sales_chart && stats.sales_chart.length > 0 ? (
              <SalesChart data={stats.sales_chart} />
            ) : (
              <div className="h-48 flex items-center justify-center text-gray-400">
                Pa gen done lavant
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stock Alert */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#001A3E]">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Stock Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <span className="text-sm font-medium text-green-700">An Stock</span>
              <span className="text-lg font-bold text-green-600">{stats?.products_in_stock || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <span className="text-sm font-medium text-yellow-700">Prèske Fini</span>
              <span className="text-lg font-bold text-yellow-600">{stats?.products_low_stock || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <span className="text-sm font-medium text-red-700">Fini</span>
              <span className="text-lg font-bold text-red-600">{stats?.products_out_of_stock || 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Order Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-[#001A3E]">Status Kòmann</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-600">{stats?.orders_pending || 0}</p>
              <p className="text-sm text-yellow-700">En Attente</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{stats?.orders_processing || 0}</p>
              <p className="text-sm text-blue-700">En Préparation</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{stats?.orders_shipped || 0}</p>
              <p className="text-sm text-purple-700">Ekspedye</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{stats?.orders_delivered || 0}</p>
              <p className="text-sm text-green-700">Livre</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Orders */}
      {stats?.recent_orders && stats.recent_orders.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-[#001A3E]">Dènye Kòmann</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Total</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Dat</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_orders.map((order) => (
                    <tr key={order.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm">{order.id?.substring(0, 8)}...</td>
                      <td className="py-3 px-4 text-sm font-medium">${order.total?.toFixed(2)}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                          order.status === 'shipped' ? 'bg-purple-100 text-purple-700' :
                          order.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
